//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import {
  IGetAudienceDailyStatsRequest,
  IGetAudienceDailyStatsResponse,
  IGetAudienceSummaryStatsRequest,
  IGetAudienceSummaryStatsResponse,
  IGetEngagementMetricsRequest,
  IGetEngagementMetricsResponse,
  IGetUserTweetsRequest,
  IGetUserTweetsResponse,
} from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import { deserializeTweetsWithMetrics } from '@apis/twitter-analytics/twitterAnalyticsApiUtils';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { Serialized } from '@utils/types/Serialized';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const TWITTER_DEFAULT_ENDPOINT = '/api/v1/';

const twitterGatewayAnalyticsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'twitter_gateway',
    `${TWITTER_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the client's Twitter summary metrics
 *
 * GET /api/v1/audience/summary-stats
 */
export const getAudienceSummaryMetrics = async (
  req: IGetAudienceSummaryStatsRequest,
  init?: RequestInit,
): Promise<IGetAudienceSummaryStatsResponse> => {
  const params = makeParams(req);
  const res = await twitterGatewayAnalyticsFetch(
    `audience/summary-stats?${params}`,
    init,
  );

  return res.json();
};

/**
 * Gets the client's Twitter audience daily metrics
 *
 * GET /api/v1/audience/stats
 */
export const getAudienceDailyMetrics = async (
  req: IGetAudienceDailyStatsRequest,
): Promise<IGetAudienceDailyStatsResponse> => {
  const params = makeParams(req);
  const res = await twitterGatewayAnalyticsFetch(`audience/stats?${params}`);

  return res.json();
};

/**
 * Gets the client's Twitter engagement metrics
 *
 * GET /api/v1/engagement/metrics
 */
export const getEngagementMetrics = async (
  req: IGetEngagementMetricsRequest,
): Promise<IGetEngagementMetricsResponse> => {
  const params = makeParams(req);
  const res = await twitterGatewayAnalyticsFetch(
    `engagement/metrics?${params}`,
  );

  return res.json();
};

/**
 * Gets the client's recent tweets with metrics
 *
 * GET /api/v1/analytics/tweets
 */
export const getUserTweets = async (
  req: IGetUserTweetsRequest,
): Promise<IGetUserTweetsResponse> => {
  const params = makeParams(req);
  const res = await twitterGatewayAnalyticsFetch(`analytics/tweets?${params}`);

  return res.json<IGetUserTweetsResponse>(deserializePinsWithMetricsResponse);
};

export const deserializePinsWithMetricsResponse = (
  response: Serialized<IGetUserTweetsResponse>,
): IGetUserTweetsResponse => {
  return {
    data: {
      tweets: deserializeTweetsWithMetrics(response.data.tweets),
      total: response.data.total,
    },
  };
};
